import compact from "lodash-es/compact";
import { Children } from "react";

import { ButtonGroup } from "@bokio/designsystem/components/Button";
import { SG } from "@bokio/designsystem/components/SpacingGroup/SpacingGroup";
import { Heading, Section } from "@bokio/designsystem/components/TypographicElements/TypographicElements";
import { LottieAnimation } from "@bokio/elements/LottieAnimation/LottieAnimation";

import type { FC, PropsWithChildren, ReactElement, ReactNode } from "react";

import * as styles from "./animatedFeedbackBlock.scss";

export const enum FeedbackAnimation {
	None = "None",
	Checkmark = "Checkmark",
	Celebration = "Celebration",
	Failure = "Failure",
}

type AnimatedSuccessFeedbackProps = PropsWithChildren<{
	title?: ReactNode | ReactElement | string;
	animation?: FeedbackAnimation;
	buttons?: (ReactElement | Falsy)[];
	testId?: string;
}>;

const AnimatedFailFeedbackGraphic: FC<{ testId?: string }> = ({ testId }) => {
	return (
		<div className={styles.animatedWrapper} data-testid={testId}>
			<div className={styles.animatedFailFeedbackCircle}>
				<strong className={styles.animatedFeedbackIcon}>!</strong>
			</div>
		</div>
	);
};

export const AnimatedFeedbackBlock = ({
	animation = FeedbackAnimation.Checkmark,
	title,
	children,
	buttons,
	testId,
}: AnimatedSuccessFeedbackProps) => {
	const hasAnimation = animation !== FeedbackAnimation.None;

	const animations: Record<FeedbackAnimation, React.JSX.Element | null> = {
		Celebration: (
			<LottieAnimation
				testId="feedback-animation-celebration"
				animation={import("@bokio/companies/src/assets/lottieAnimations/debbie-happy-jump-animation")}
				height="200px"
			/>
		),
		Checkmark: (
			<LottieAnimation
				testId="feedback-animation-checkmark"
				animation={import("@bokio/companies/src/assets/lottieAnimations/checkmark")}
				height="200px"
			/>
		),
		Failure: <AnimatedFailFeedbackGraphic testId="feedback-animation-failure" />,
		None: null,
	};

	return (
		<div className={styles.wrapper} data-testid={testId}>
			<Section>
				<SG gap="24">
					{hasAnimation && <div data-testid="feedback-animation">{animations[animation]}</div>}

					{!!title && (
						<Heading textCenter style="large-heading" testId="animated-feedback-block--Title">
							{title}
						</Heading>
					)}

					{children && <div data-testid="animated-feedback-block--Content">{children}</div>}

					{!!buttons?.length && (
						<div className={styles.buttons} data-testid="animated-feedback-block--Buttons">
							<ButtonGroup align="center" direction="column">
								{Children.map(compact(buttons), button => button)}
							</ButtonGroup>
						</div>
					)}
				</SG>
			</Section>
		</div>
	);
};
